import axios from "axios";

/**
 * Global session-expiry handling.
 *
 * The learner JWT (member_token) expires after ~1h. When it does:
 *   - community-* endpoints reply { error: 1, expired: 1, msg } over HTTP 200
 *   - native endpoints (courselist / lesson-details / profile…) reply HTTP 401
 *
 * Either way we clear the stored member and bounce to the community's login
 * page so the user can sign in again, instead of leaving them on a half-broken
 * signed-in UI. A guard prevents redirect loops / duplicate redirects.
 */
let redirecting = false;

// Community token from the current URL: /membership/:token/... or /:token.
function tokenFromPath() {
  const p = window.location.pathname;
  const m = p.match(/^\/membership\/([^/]+)/) || p.match(/^\/([^/]+)/);
  return m ? m[1] : null;
}

function handleSessionExpired() {
  if (redirecting) return;
  const token = tokenFromPath();
  if (!token) return;
  // Already on the login page — just clear, don't loop.
  if (/\/login\/?$/.test(window.location.pathname)) {
    try {
      localStorage.removeItem(`cm_${token}`);
    } catch {
      /* ignore */
    }
    return;
  }
  redirecting = true;
  try {
    localStorage.removeItem(`cm_${token}`);
  } catch {
    /* ignore */
  }
  // Full navigation (not SPA) so all in-memory state is dropped cleanly.
  // ?expired=1 lets the login page show a "your session expired" notice.
  window.location.assign(`/membership/${token}/login?expired=1`);
}

const isExpiredEnvelope = (data) =>
  data &&
  data.error === 1 &&
  (data.expired === 1 ||
    /session (has )?expired|log\s?in again/i.test(String(data.msg || "")));

export default function setupAxios() {
  /*
   * Request timeout.
   *
   * Without one, axios waits forever. A slow endpoint therefore showed as a
   * spinner that never stopped: every loader clears its state in `finally`,
   * but `finally` does not run until the request settles — so the page sat
   * loading until the GATEWAY gave up (504), often a minute later, and on the
   * courses page it looked like the app had hung.
   *
   * 25s is deliberately longer than any healthy call (production TTFB for
   * these endpoints is ~250ms) and shorter than the gateway's own timeout, so
   * a stalled request surfaces HERE as an ordinary axios error the existing
   * catch blocks already handle, rather than as a dead page.
   *
   * This does NOT make anything faster — the slow course-list query is a
   * separate, server-side fix. It only stops a slow response presenting as a
   * broken one.
   */
  axios.defaults.timeout = 25000;

  axios.interceptors.response.use(
    (response) => {
      if (isExpiredEnvelope(response.data)) handleSessionExpired();
      return response;
    },
    (error) => {
      if (error.response?.status === 401) handleSessionExpired();
      return Promise.reject(error);
    }
  );
}
